import { PixelPoint } from 'src/app/interfaces/shared/calibration';
import { LatLon } from 'src/app/interfaces/shared/latLon';

export class CalibrationMath {
    /**
     * Calcule la distance en pixels entre deux points
     * @param p1 Point 1 {x, y}
     * @param p2 Point 2 {x, y}
     * @returns Distance en pixels
     */
    static pixelDistance(p1: PixelPoint, p2: PixelPoint): number {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Calcule la distance réelle entre deux points GPS (Haversine)
     * @param coord1 {lat, lon}
     * @param coord2 {lat, lon}
     * @returns Distance en mètres
     */
    static realDistance(coord1: LatLon, coord2: LatLon): number {
        const R = 6371000; // Rayon terrestre en mètres
        const φ1 = (coord1.lat * Math.PI) / 180;
        const φ2 = (coord2.lat * Math.PI) / 180;
        const Δφ = ((coord2.lat - coord1.lat) * Math.PI) / 180;
        const Δλ = ((coord2.lon - coord1.lon) * Math.PI) / 180;

        const a =
            Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    /**
     * Calcule l'échelle du calibrage
     * scale = distance_réelle / distance_pixels
     * @param p1Pixels Premier point en pixels
     * @param p2Pixels Deuxième point en pixels
     * @param p1Real Coords réelles du premier point
     * @param p2Real Coords réelles du deuxième point
     * @returns Échelle (mètres par pixel)
     */
    static calculateScale(
        p1Pixels: PixelPoint,
        p2Pixels: PixelPoint,
        p1Real: LatLon,
        p2Real: LatLon
    ): number {
        const pixelDist = this.pixelDistance(p1Pixels, p2Pixels);

        if (pixelDist === 0) {
            throw new Error('viewer.calibration.error.same_pixels');
        }

        const realDist = this.realDistance(p1Real, p2Real);
        return realDist / pixelDist;
    }

    /**
     * Calcule l'angle de rotation entre image et réalité
     * @param p1Pixels Point 1 en pixels
     * @param p2Pixels Point 2 en pixels
     * @param p1Real Coords réelles point 1
     * @param p2Real Coords réelles point 2
     * @returns Angle en degrés (0-360)
     */
    static calculateAngle(p1Pixels: PixelPoint, p2Pixels: PixelPoint, p1Real: LatLon, p2Real: LatLon): number {
        // Vecteur pixel dans le repère image (Y vers le bas)
        const dxPix = p2Pixels.x - p1Pixels.x;
        const dyPix = -(p2Pixels.y - p1Pixels.y);

        // Vecteur réel dans un repère local centré sur P1
        const [dxReal, dyReal] = this.gpsToXY(
            p2Real.lat,
            p2Real.lon,
            p1Real.lat,
            p1Real.lon
        );

        const anglePix = Math.atan2(dyPix, dxPix);
        const angleReal = Math.atan2(dyReal, dxReal);

        const angleDeg = (angleReal - anglePix) * 180 / Math.PI;

        return ((angleDeg + 180) % 360 + 360) % 360 - 180;
    }

    /**
     * Applique une transformation affine à un point pixel
     * Utile pour prévisualiser la position des 4 coins de l'image
     * @param pixel Point pixel à transformer
     * @param centerPixel Centre de rotation
     * @param scale Échelle
     * @param angleRad Angle en radians
     * @returns Point transformé
     */
    static transformPixel(
        pixel: PixelPoint,
        centerPixel: PixelPoint,
        scale: number,
        angleRad: number
    ): PixelPoint {
        // Translater au centre
        let x = pixel.x - centerPixel.x;
        let y = pixel.y - centerPixel.y;

        // rotation + scale
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);

        const newX = (x * cos - y * sin) * scale;
        const newY = (x * sin + y * cos) * scale;

        return {
            x: newX + centerPixel.x,
            y: newY + centerPixel.y,
        };
    }

    /**
     * Calcule les 4 coins de l'image calibrée en lat/lon
     * @param centerLatLon Centre de l'image
     * @param imageWidth Largeur en pixels
     * @param imageHeight Hauteur en pixels
     * @param scale Échelle (mètres/pixel)
     * @param angleRad Angle en radians
     * @returns {north, south, east, west}
     */
    static calculateMapCoord(
        centerLatLon: LatLon,
        imageWidth: number,
        imageHeight: number,
        scale: number,
        angleRad: number
    ): { north: number; south: number; east: number; west: number } {
        const centerXY = this.gpsToXY(centerLatLon.lat, centerLatLon.lon, 0, 0);

        const centerX = centerXY[0] ?? 0;
        const centerY = centerXY[1] ?? 0;

        // Les 4 coins en pixels (relatif au centre)
        const corners: PixelPoint[] = [
            { x: -imageWidth / 2, y: -imageHeight / 2 }, // Top-left
            { x: imageWidth / 2, y: -imageHeight / 2 },  // Top-right
            { x: imageWidth / 2, y: imageHeight / 2 },   // Bottom-right
            { x: -imageWidth / 2, y: imageHeight / 2 },  // Bottom-left
        ];

        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);

        const transformedCorners = corners.map((c) => ({
            x: (c.x * cos - c.y * sin) * scale + centerX,
            y: (c.x * sin + c.y * cos) * scale + centerY,
        }));

        const latLons = transformedCorners.map((c) => {
            const deltaX = c.x - centerX;
            const deltaY = c.y - centerY;
            return this.xyToGps(deltaX, deltaY, centerLatLon.lat, centerLatLon.lon);
        });

        const lats = latLons.map((ll) => ll[1]).filter((lat): lat is number => lat !== undefined);

        const lons = latLons.map((ll) => ll[0]).filter((lon): lon is number => lon !== undefined);

        if (lats.length === 0 || lons.length === 0) {
            throw new Error('viewer.calibration.error.map_coords');
        }

        return {
            north: Math.max(...lats),
            south: Math.min(...lats),
            east: Math.max(...lons),
            west: Math.min(...lons),
        };
    }

    /**
 * Convertit GPS en coordonnées XY (projection équirectangulaire)
 * @param lat Latitude en degrés
 * @param lon Longitude en degrés
 * @param originLat Origine latitude (optionnel, défaut 0)
 * @param originLon Origine longitude (optionnel, défaut 0)
 * @returns [x, y] en mètres relatifs à l'origine
 */
    static gpsToXY(lat: number, lon: number, originLat: number = 0, originLon: number = 0): [number, number] {
        const R = 6378137;
        const originLatRad = originLat * Math.PI / 180;

        const x = (lon - originLon) * Math.PI / 180 * R * Math.cos(originLatRad);
        const y = (lat - originLat) * Math.PI / 180 * R;

        return [x, y];
    }

    /**
     * Convertit XY en GPS (projection équirectangulaire inverse)
     * @param x Position X en mètres (relative à l'origine)
     * @param y Position Y en mètres (relative à l'origine)
     * @param originLat Origine latitude
     * @param originLon Origine longitude
     * @returns [lon, lat] en degrés
     */
    static xyToGps(x: number, y: number, originLat: number = 0, originLon: number = 0): [number, number] {
        const R = 6378137;
        const originLatRad = originLat * Math.PI / 180;

        const dLat = y / R;
        const dLon = x / (R * Math.cos(originLatRad));

        const lat = originLat + dLat * 180 / Math.PI;
        const lon = originLon + dLon * 180 / Math.PI;

        return [lon, lat];
    }

    /**
     * Valide les deux points de calibrage
     * @returns Message d'erreur ou null si valide
     */
    static validateCalibrationPoints(
        p1: { pixel: PixelPoint; real: LatLon },
        p2: { pixel: PixelPoint; real: LatLon }
    ): string | null {
        if (p1.pixel.x === p2.pixel.x && p1.pixel.y === p2.pixel.y) {
            return 'viewer.calibration.error.same_pixels';
        }

        if (p1.real.lat === p2.real.lat && p1.real.lon === p2.real.lon) {
            return 'viewer.calibration.error.same_coords';
        }

        if (Math.abs(p1.real.lat) > 90 || Math.abs(p2.real.lat) > 90) {
            return 'viewer.calibration.error.invalid_lat';
        }

        if (Math.abs(p1.real.lon) > 180 || Math.abs(p2.real.lon) > 180) {
            return 'viewer.calibration.error.invalid_lon';
        }

        return null;
    }
}